import { PatientProduct } from "services/types/patient-products.type";
import { CSVReportResponse } from "services/types/reports.type";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import {
  formatPatient,
  formatPatientFilters,
  getPatientsEndpointWithQueryParameters
} from "utils/patients.util";
import { createPaginatedEndpoint } from "utils/services.util";

import { PaginatedResponse } from "./common";
import { emptySplitApi } from "./empty-split-api";
import {
  Card,
  Patient,
  PatientFilters,
  PatientInput,
  PatientMergeDetails,
  PatientPhotoInput,
  PatientSummary,
  PatientSummaryType,
  PatientsDownloadQuery,
  PatientsResponse,
  QuickBooksCustomerDetails,
  SimilarPatient
} from "./types/patients.type";
import { QuickBooksInvoicesResponse } from "./types/quickbooks-invoice.type";

export const DEFAULT_PATIENTS_LIMIT = 25;
export const DEFAULT_PATIENTS_QUERY =
  "{id,first_name,middle_name,last_name,full_name,is_active,primary_care_physician{full_name},referring_physician{full_name},referral_source{id,name,referred_type},sub_referral_source{id,name,object_id,object_type,parent{id}},preferred_name,title,birthdate,age_years,photo,suffix,email,phone,secondary_phone,crm_segment,box_folder_id,unread_inbound_message_count,preferred_communication_method,phone_type,primary_contact_notes,secondary_contact_notes,city,state,preferred_clinic{id,name,display_name,timezone,region{id,name}},preferred_provider{id,first_name,last_name,full_name,title,suffix,is_active},previous_appointment_moment,next_appointment_moment,previous_appointment{id,type{name},status,start_moment,clinic{timezone},patient_feedback_rating,staff_member{first_name,last_name}},following_appointment_moment,following_appointment{id,type{id,name},needs_reschedule,title,start_moment,clinic{name,timezone},status,patient_feedback_rating,staff_member{first_name,last_name}},next_appointment{id,type{name},status,start_moment,clinic{timezone},patient_feedback_rating,staff_member{first_name,last_name}},managed_care_plan{id,name,notes,managed_care_provider{name}},assigned_tags,created_at_display,last_accessed,do_not_email,do_not_message}";

export const PATIENT_SEARCH_QUERY =
  "{id,first_name,middle_name,last_name,full_name,preferred_name,is_active,birthdate,age_years,email,phone,secondary_phone,city,state,photo,preferred_provider{full_name},preferred_clinic{id,name,region{name}},box_folder_id}";

export const PATIENT_SEARCH_QUERY_WITH_ALERT_COUNT =
  "{id,first_name,middle_name,last_name,full_name,preferred_name,is_active,birthdate,age_years,email,phone,secondary_phone,city,state,photo,preferred_provider{full_name},preferred_clinic{id,name,region{name}},box_folder_id,active_alert_count}";

export const PATIENT_RESPONSIBLE_PARTY_QUERY =
  "{id,full_name,first_name,middle_name,last_name,email,street_address_1,street_address_2,city,state,zip_code,responsible_party_relationship,responsible_party_first_name,responsible_party_last_name,responsible_party_street_address_1,responsible_party_street_address_2,responsible_party_city,responsible_party_state,responsible_party_country,responsible_party_zip_code,responsible_party_phone,responsible_party_email,bill_to_responsible_party,preferred_clinic{id,timezone,name,logo,display_name,extra{billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_zip,billng_phone},street_address_1,street_address_2,city,state,zip_code,phone,region{id}}}";

export const patientsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    queryPatients: builder.query<Patient[], PatientFilters>({
      query: (filters) => getPatientsEndpointWithQueryParameters(filters),
      providesTags: ["Patients"],
      transformResponse: (response: PatientsResponse) => {
        return response.results.map(formatPatient);
      }
    }),
    getPatientsPaginated: createPaginatedEndpoint(builder, {
      queryFn: (filters: PatientFilters) =>
        getPatientsEndpointWithQueryParameters(filters),
      transformResponseFn: (response: PatientsResponse) =>
        response.results.map(formatPatient),
      providesTags: ["Patients"]
    }),
    queryPatientsWithEmailsAndPhoneCount: builder.query<
      { count: number; count_sms: number; count_distinct_patients: number },
      PatientFilters
    >({
      query: (filters) =>
        `/patients/patients-count/${formatValidQueryParamUrlCommaSeparated(
          formatPatientFilters(filters),
          true
        )}`,
      providesTags: ["Patients"],
      transformResponse: (response: {
        count: number;
        count_sms: number;
        count_distinct_patients: number;
      }) => ({
        count: Number(response.count),
        count_sms: Number(response.count_sms),
        count_distinct_patients: Number(response.count_distinct_patients)
      })
    }),
    queryPatientsCounter: builder.query<
      number,
      Omit<PatientFilters, "query" | "limit" | "offset">
    >({
      query: (filters) =>
        `/patients/count/?${formatValidQueryParamUrlCommaSeparated(
          formatPatientFilters({
            ...filters,
            baseFilter: {
              is_active: filters?.is_active,
              ...filters.baseFilter
            }
          } as PatientFilters)
        )}`,
      providesTags: ["Patients"]
    }),
    getPatient: builder.query<Patient, { patientId: number; query?: string }>({
      query: ({ patientId, query = "{}" }) =>
        `/patients/${patientId}/?query=${query}`,
      providesTags: (result, _, params) => [
        "Patients",
        {
          type: "Patient" as const,
          id: params.patientId
        }
      ],
      transformResponse: (response: Patient) => formatPatient(response),
      async onQueryStarted(
        { patientId },
        { dispatch, getState, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          const now = new Date().toISOString();
          const state = getState() as any;
          const queries = state.api?.queries ?? {};
          const targetEndpoints = ["getPatientsPaginated", "queryPatients"];

          for (const entry of Object.values(queries)) {
            const { endpointName, status, originalArgs } = entry as any;
            if (
              !targetEndpoints.includes(endpointName) ||
              status !== "fulfilled" ||
              !originalArgs
            )
              continue;

            const ordering: string | undefined = originalArgs?.ordering;
            const orderingFields = ordering ? ordering.split(",") : [];
            const sortByLastAccessed = orderingFields.some(
              (f) => f === "last_accessed" || f === "-last_accessed"
            );

            const sortPatients = (patients: Patient[]) => {
              if (!sortByLastAccessed) return;
              patients.sort((a: Patient, b: Patient) => {
                for (const field of orderingFields) {
                  const descending = field.startsWith("-");
                  const key = descending ? field.slice(1) : field;
                  const aVal = (a as any)[key];
                  const bVal = (b as any)[key];

                  let cmp = 0;
                  if (aVal == null && bVal == null) cmp = 0;
                  else if (aVal == null) cmp = -1;
                  else if (bVal == null) cmp = 1;
                  else if (
                    typeof aVal === "number" &&
                    typeof bVal === "number"
                  ) {
                    cmp = aVal - bVal;
                  } else {
                    cmp = String(aVal).localeCompare(String(bVal));
                  }

                  if (cmp !== 0) return descending ? -cmp : cmp;
                }
                return 0;
              });
            };

            if (endpointName === "getPatientsPaginated") {
              dispatch(
                patientsApi.util.updateQueryData(
                  "getPatientsPaginated" as any,
                  originalArgs,
                  (draft: any) => {
                    for (const pageKey of Object.keys(draft)) {
                      if (pageKey === "lastFetchedPage") continue;
                      const patient = draft[pageKey]?.find(
                        (p: Patient) => p.id === patientId
                      );
                      if (patient) {
                        patient.last_accessed = now;
                      }
                      sortPatients(draft[pageKey]);
                    }
                  }
                )
              );
            }

            if (endpointName === "queryPatients") {
              dispatch(
                patientsApi.util.updateQueryData(
                  "queryPatients" as any,
                  originalArgs,
                  (draft: any) => {
                    const patient = draft.find(
                      (p: Patient) => p.id === patientId
                    );
                    if (patient) {
                      patient.last_accessed = now;
                    }
                    sortPatients(draft);
                  }
                )
              );
            }
          }
        } catch {
          // Query failed, do nothing
        }
      }
    }),
    getPatientCards: builder.query<Card[], number>({
      query: (patientId) => `/patients/${patientId}/cards/`
    }),
    deletePatientCard: builder.mutation<
      Card[],
      { patientId: number; cardId: string }
    >({
      query: ({ patientId, cardId }) => ({
        url: `/patients/${patientId}/cards/${cardId}/`,
        method: "DELETE"
      })
    }),
    createPatientCard: builder.mutation<
      Card,
      { patientId: number; payment_method_id: string }
    >({
      query: ({ patientId, payment_method_id }) => ({
        url: `/patients/${patientId}/cards/`,
        method: "POST",
        body: { payment_method_id }
      })
    }),
    // Gravity/emergepay card-on-file: persist the CreditSaveCard token (uniqueTransId) + display
    // metadata. Mirrors the /cards/ create endpoint's Gravity branch.
    createGravityPatientCard: builder.mutation<
      Card,
      {
        patientId: number;
        unique_trans_id: string;
        last4: string;
        card_type: string;
        exp_month: number;
        exp_year: number;
        holder_name?: string;
      }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/patients/${patientId}/cards/`,
        method: "POST",
        body
      })
    }),
    createGravityCheckoutSession: builder.mutation<
      { transaction_token: string },
      { patientId: number; clinic_id: number; amount: number | string }
    >({
      query: ({ patientId, clinic_id, amount }) => ({
        url: `/patients/${patientId}/gravity-checkout-session/`,
        method: "POST",
        // emergepay needs the charge amount at start-transaction time (BE serializer requires it).
        body: { clinic_id, amount }
      })
    }),
    addPatient: builder.mutation<Patient, PatientInput>({
      query: (patientData) => ({
        url: "/patients/",
        method: "POST",
        body: patientData
      }),
      transformResponse: (response: Patient) => formatPatient(response),
      invalidatesTags: ["Patients"]
    }),
    updatePatient: builder.mutation<Patient, Partial<PatientInput>>({
      query: ({ id, ...patientData }) => ({
        url: `/patients/${id}/`,
        method: "PATCH",
        body: patientData
      }),
      transformResponse: (response: Patient) => formatPatient(response),
      invalidatesTags: (result, error, arg, meta) => {
        return error
          ? []
          : ["Patients", "Events", "NoahPatient", "MergeTagsPatient"];
      }
    }),
    deactivatePatientAndCancelAppointments: builder.mutation<
      Patient,
      Partial<PatientInput>
    >({
      query: ({ id, ...patientData }) => ({
        url: `/patients/${id}/deactivate-with-appointments-cancelation/`,
        method: "PATCH",
        body: patientData
      }),
      transformResponse: (response: Patient) => formatPatient(response),
      invalidatesTags: ["Patients", "Appointments", "Events"]
    }),
    updatePatientPhoto: builder.mutation<Patient, PatientPhotoInput>({
      query: ({ id, formData }) => ({
        url: `/patients/${id}/`,
        method: "PATCH",
        body: formData
      }),
      transformResponse: (response: Patient) => formatPatient(response),
      invalidatesTags: ["Patients"]
    }),
    downloadPatientsCSV: builder.query<
      CSVReportResponse,
      PatientsDownloadQuery
    >({
      query: ({ csvHeader = "*", csvLabels, ...filters }) => {
        const url =
          getPatientsEndpointWithQueryParameters(filters) +
          `&csv_header=${csvHeader}&csv_labels=${csvLabels}&current_clinic=${filters.current_clinic}`;

        return {
          url: url,
          headers: { Accept: "custom-csv/json" }
        };
      }
    }),
    updatePatientProductsDeliverHearingAids: builder.mutation<
      PatientProduct[],
      {
        patientId: number;
        delivered_at: string;
      }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/patients/${patientId}/patient-products/deliver/`,
        method: "POST",
        body
      })
    }),
    deactivatePatients: builder.mutation<void, number[]>({
      query: (ids) => ({
        url: `/patients/bulk-deactivate/`,
        method: "PATCH",
        body: { ids },
        invalidatesTags: ["Patients"]
      })
    }),
    bulkDeactivatePatientsByFilter: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      { patients_filter: Record<string, any> }
    >({
      query: (body) => ({
        url: `/patients/bulk-deactivate-by-filter/`,
        method: "POST",
        body
      })
    }),
    mergeDetails: builder.query<
      [PatientMergeDetails, PatientMergeDetails],
      { source_patient_id: number; dest_patient_id: number }
    >({
      query: (params) => ({ url: `/patients/merge-details/`, params })
    }),
    mergePatients: builder.mutation<
      void,
      { source_patient_id: number; dest_patient_id: number }
    >({
      query: (body) => ({
        url: `/patients/merge/`,
        method: "PATCH",
        body,
        invalidatesTags: ["Patients"]
      })
    }),
    addPatientPhysician: builder.mutation<
      void,
      { patientId: number; physicianId: number; physicianType: string }
    >({
      query: ({ patientId, physicianId, physicianType }) => ({
        url: `/patients/${patientId}/other-physician/`,
        method: "POST",
        body: { physician_id: physicianId, physician_type: physicianType }
      }),
      invalidatesTags: ["Patients", "MergeTagsPatient"]
    }),
    removePatientPhysician: builder.mutation<
      void,
      { patientId: number; physicianId: number; physicianType: string }
    >({
      query: ({ patientId, physicianId }) => ({
        url: `/patients/${patientId}/other-physician/?physician_id=${physicianId}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Patients"]
    }),
    getPatientSummary: builder.query<
      PatientSummary | null,
      { patientId: number; type: PatientSummaryType }
    >({
      query: ({ patientId, type }) => ({
        url: `/patients/${patientId}/summary/`,
        params: {
          limit: 1,
          type: type
        }
      }),
      transformResponse: (response: PaginatedResponse<PatientSummary>) => {
        return response.results[0] ?? null;
      },
      providesTags: (result, error, { type }) => [
        { id: type, type: "PatientSummary" }
      ]
    }),
    generatePatientSummary: builder.mutation<
      PatientSummary,
      { patientId: number; type: PatientSummaryType; current_clinic: number }
    >({
      query: ({ patientId, type, current_clinic }) => ({
        url: `/patients/${patientId}/summary/generate/`,
        body: { type },
        params: { current_clinic },
        method: "POST"
      }),
      invalidatesTags: (result, error, { type }) => [
        { id: type, type: "PatientSummary" }
      ]
    }),
    sendCaptionCallLead: builder.mutation<
      void,
      { patientId: number; clinic: number }
    >({
      query: ({ patientId, clinic }) => ({
        url: `/patients/${patientId}/caption-call-leads/`,
        method: "POST",
        body: { clinic }
      }),
      invalidatesTags: ["Patients", "Patient"]
    }),
    getPatientQuickBooksInvoices: builder.query<
      QuickBooksInvoicesResponse,
      { patientId: number; limit?: number }
    >({
      query: ({ patientId, limit = 50 }) => ({
        url: `/patients/${patientId}/quickbooks-invoices/`,
        params: { limit }
      })
    }),
    getPatientQuickBooksDetails: builder.query<
      QuickBooksCustomerDetails | null,
      number
    >({
      query: (patientId) => ({
        url: `/patients/${patientId}/quickbooks-details/`
      }),
      providesTags: (result, error, patientId) => [
        "PatientQuickBooksDetails",
        {
          type: "PatientQuickBooksDetails" as const,
          id: patientId
        }
      ]
    }),
    recordAccess: builder.mutation<void, number>({
      query: (patientId) => ({
        url: `/patients/${patientId}/record-access/`,
        method: "POST"
      })
    }),
    fiveKeysEnroll: builder.mutation<
      { status: string },
      {
        patientId: number;
        email: string;
        fullname: string;
        provider_name: string;
      }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/patients/${patientId}/five-keys-enroll/`,
        method: "POST",
        body
      })
    }),
    searchSimilarPatients: builder.query<
      SimilarPatient[],
      {
        first_name?: string;
        last_name?: string;
        phone?: string;
        email?: string;
        suno_comms_id?: string;
      }
    >({
      query: (params) =>
        `/patients/search-similar/?${formatValidQueryParamUrlCommaSeparated(params)}`
    }),
    getPatientPortalUrl: builder.mutation<
      { url: string },
      { patientId: number; page?: string }
    >({
      query: ({ patientId, page }) => ({
        url: `/patients/${patientId}/portal-url/`,
        method: "POST",
        body: {
          ...(page && { page })
        }
      })
    })
  })
});

export const {
  useQueryPatientsQuery,
  useQueryPatientsWithEmailsAndPhoneCountQuery,
  useLazyQueryPatientsQuery,
  useLazyQueryPatientsCounterQuery,
  useAddPatientMutation,
  useUpdatePatientMutation,
  useDeactivatePatientAndCancelAppointmentsMutation,
  useGetPatientQuery,
  useLazyGetPatientQuery,
  useGetPatientCardsQuery,
  useDeletePatientCardMutation,
  useCreatePatientCardMutation,
  useCreateGravityPatientCardMutation,
  useCreateGravityCheckoutSessionMutation,
  useUpdatePatientPhotoMutation,
  useLazyDownloadPatientsCSVQuery,
  useUpdatePatientProductsDeliverHearingAidsMutation,
  useDeactivatePatientsMutation,
  useBulkDeactivatePatientsByFilterMutation,
  useMergeDetailsQuery,
  useMergePatientsMutation,
  useAddPatientPhysicianMutation,
  useRemovePatientPhysicianMutation,
  useGetPatientSummaryQuery,
  useGeneratePatientSummaryMutation,
  useSendCaptionCallLeadMutation,
  useGetPatientQuickBooksInvoicesQuery,
  useLazyGetPatientQuickBooksInvoicesQuery,
  useGetPatientQuickBooksDetailsQuery,
  useRecordAccessMutation,
  useFiveKeysEnrollMutation,
  useSearchSimilarPatientsQuery,
  useGetPatientPortalUrlMutation
} = patientsApi;
